import { onMounted, ref } from 'vue';
import { API, graphqlOperation } from 'aws-amplify';

import { awsNow, logintest } from '@/lib/common';
import { getFrequentTags, storeFrequentTags } from '@/lib/rankings';

import { listTagsWithCourse } from '@/graphql/custom';
import { listLastShowRankingByTag, listDisplayTopRecommends, getCompany } from '@/graphql/queries';
import { config } from '@/config';
import { Company, Tag, User, DisplayTopRecommend } from '@/@types/d';

export const HomeViewSetup = () => {
  const currentUser = ref({} as User);
  const loggedIn = ref(false);
  const isloading = ref(false);

  const tags = ref<Tag[]>([]);
  const frequentTags = ref([]);
  const topRecommends = ref<DisplayTopRecommend[]>([]);

  const recommendTitle = ref('');
  const adPermission = ref(false);

  onMounted(async () => {
    logintest(initView, clearView, {user:currentUser, state:loggedIn});
  });


  const initView = async () => {
    isloading.value = true;
    queryListCategoryTags();
    queryListFrequentTags();
    queryDisplayTopRecommends();
    initCompanyState();
    isloading.value = false;
  };
  const clearView = () => {
    tags.value = [];
    frequentTags.value = [];
  };

  // タグリスト取得 query（ページネーション対応）
  const queryListCategoryTags = async () => {
    const allItems: Tag[] = [];
    let nextToken: string | null = null;
    
    do {
      const params = {
        filter: {type: {eq: 'category'}},
        limit: config.TAGLIST_QUERYLIMIT,
        ...(nextToken && { nextToken })
      };
      
      let res: any;
      try {
        res = await API.graphql(graphqlOperation(listTagsWithCourse, params));
      } catch(err) {
        res = err;
        console.warn('カテゴリタグリスト取得エラー:', err);
      }
      
      if (res?.data?.listTags?.items) {
        allItems.push(...res.data.listTags.items);
        nextToken = res.data.listTags.nextToken;
      } else {
        break;
      }
    } while (nextToken);
    
    // コースが存在するタグのみをフィルタリング
    tags.value = allItems.filter((t: any) => t.courses.items.filter((c: any) => c).length > 0);
  };

  const queryListFrequentTags = async () => {
    if(!currentUser.value || currentUser.value.company == '') return;

    const freq = getFrequentTags(currentUser.value.company||'');
    if(freq.length > 0) {
      frequentTags.value = freq;
      return;
    }

    const params = {
      companyId: currentUser.value.company,
      tagType: 'tag',
      limit: config.FREQUENT_TAG_MAX,
    };
    let res:any;
    try {
      // listLastShowRankingByTag: 直近1か月のアクセス履歴に基づくランキング
      res = await API.graphql(graphqlOperation(listLastShowRankingByTag, params));
    } catch(err) {
      res = err;
      console.warn(err);
    }
    if(res.data.listLastShowRankingByTag) {
      // 講座アクセス権確認不要（アクセス実績から取るので）
      const freq = res.data.listLastShowRankingByTag.items.map((t:any) => {
        return {
          id: t.tagId,
          label: t.tagLabel,
          type: t.tagType
        } as Tag;
      });
      storeFrequentTags(currentUser.value.company||'', freq);
      frequentTags.value = freq;
    }
  }

  // トップのおすすめコンテンツリストセット query（ページネーション対応）
  const queryDisplayTopRecommends = async () => {
    const tp = awsNow();
    const allItems: DisplayTopRecommend[] = [];
    let nextToken: string | null = null;
    
    do {
      const params = {
        filter: {and: {displayFrom: {le: tp}, displayTo: {ge: tp}}},
        limit: config.DISPLAYTOP_QUERYLIMIT,
        ...(nextToken && { nextToken })
      };
      
      let res: any;
      try {
        res = await API.graphql(graphqlOperation(listDisplayTopRecommends, params));
      } catch(err) {
        res = err;
        console.warn('トップおすすめコンテンツ取得エラー:', err);
      }
      
      if (res?.data?.listDisplayTopRecommends?.items) {
        allItems.push(...res.data.listDisplayTopRecommends.items);
        nextToken = res.data.listDisplayTopRecommends.nextToken;
      } else {
        break;
      }
    } while (nextToken);
    
    topRecommends.value = allItems;
  };

  const initCompanyState = async () => {
    if(currentUser.value.company) {
      try {
        const res: any = await API.graphql(graphqlOperation(getCompany, {id: currentUser.value.company}));
        if(res) {
          const c: Company = res.data.getCompany;
          recommendTitle.value = c.recommendTitle || 'おすすめ講座';
          adPermission.value = !!c.adPermission;
        }
      } catch (e) {
        console.error('init company state error');
        throw e;
      }
    }
  };

  // カテゴリタグリストをソートし返す
  const listCategoryTags = (): Tag[] => {
    return tags.value.sort((a:any, b:any) => (a.order - b.order));
  };
  const listFrequentTags = (): Tag[] => {
    return frequentTags.value;
  };
  const listTopRecommends = (): DisplayTopRecommend[] => {
    return topRecommends.value.sort((a:any, b:any) => (a.order - b.order));
  };

  return {
    isloading,
    listCategoryTags,
    listFrequentTags,
    listTopRecommends,
    currentUser,
    recommendTitle,
    adPermission
  };
};
